import React from "react";
import { Box, Paper, Typography } from "@mui/material";
import { SubdirectoryArrowRightIcon } from "../../icons";
import { observer } from "mobx-react";
import { WorkflowRoadmap } from "./WorkflowRoadmap";
import { WorkflowDefinition } from "./workflowRoadmapTypes";
import { useRootStore } from "../../store/contexts";

export interface CombinedRoadmapBranch {
  workflow: WorkflowDefinition;
  currentState: string;
  labels: Record<string, string>;
  /** Motivo operacional del último error (p.ej. dndaLastOperationalError):
   * se muestra bajo el roadmap para que el usuario vea la causa accionable,
   * no solo el label genérico del estado. */
  operationalError?: string | null;
  /** Advertencia neutra accionable por el usuario (se muestra también con
   * concealErrors — ver WorkflowRoadmap.notice). */
  notice?: string | null;
}

export interface CombinedWorkflowRoadmapProps {
  primary: CombinedRoadmapBranch;
  branches: CombinedRoadmapBranch[];
  /** Presentación sin errores para usuarios que no operan /automation
   * (ver WorkflowRoadmap.concealErrors). Aplica a TODAS las ramas. */
  concealErrors?: boolean;
}

// Dibuja un workflow primario y, debajo, cada workflow dependiente justificado a
// la izquierda con un encabezado "↳ desde <nodo de empalme>". El nodo de empalme
// sale de branch.workflow.branchFrom.state, resuelto contra los labels del primario.
export const CombinedWorkflowRoadmap: React.FC<CombinedWorkflowRoadmapProps> = observer(({
  primary,
  branches,
  concealErrors = false,
}) => {
  const { langStore } = useRootStore();
  return (
    <Box sx={{ width: "100%" }}>
      <WorkflowRoadmap
        workflow={primary.workflow}
        currentState={primary.currentState}
        labels={primary.labels}
        operationalError={primary.operationalError}
        concealErrors={concealErrors}
        notice={primary.notice}
        transitions={[]}
        onTransition={() => {}}
      />
      {branches.map((b) => {
        const fromState = b.workflow.branchFrom?.state;
        const fromLabel = fromState ? primary.labels[fromState] || fromState : "";
        // .branchDescription del DSL: texto que explica el empalme, con peso
        // visual (callout). Sin descripción, cae al genérico "desde <estado>".
        const branchDescription =
          b.workflow.branchFrom?.description?.[langStore.language]?.s ?? null;
        return (
          <Box
            key={b.workflow.id}
            sx={{ mt: 2, pl: 1, borderLeft: "2px solid", borderColor: "primary.light" }}
          >
            {branchDescription ? (
              <Paper
                variant="outlined"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1,
                  px: 1.5,
                  py: 1,
                  maxWidth: 640,
                  backgroundColor: "primary.50",
                  borderColor: "primary.light",
                }}
              >
                <SubdirectoryArrowRightIcon fontSize="small" color="primary" sx={{ mt: 0.25 }} />
                <Typography variant="body2">{branchDescription}</Typography>
              </Paper>
            ) : (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, color: "text.secondary" }}
              >
                <SubdirectoryArrowRightIcon fontSize="small" />
                <Typography variant="caption">desde {fromLabel}</Typography>
              </Box>
            )}
            <WorkflowRoadmap
              workflow={b.workflow}
              currentState={b.currentState}
              labels={b.labels}
              operationalError={b.operationalError}
              concealErrors={concealErrors}
              notice={b.notice}
              transitions={[]}
              onTransition={() => {}}
            />
          </Box>
        );
      })}
    </Box>
  );
});
